import { Component } from '@theme/component';

const GROUP_SIZE = 3;
const AUTO_ADVANCE_INTERVAL_MS = 5000;

/**
 * A testimonials carousel that auto-advances through cards in groups of
 * GROUP_SIZE every AUTO_ADVANCE_INTERVAL_MS, looping back to the start.
 * Touch/trackpad swipe comes for free from native scroll-snap on the strip;
 * this adds prev/next buttons, the autoplay loop, and pause-on-interaction.
 *
 * @typedef {object} TestimonialsCarouselRefs
 * @property {HTMLElement} scroller
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<TestimonialsCarouselRefs>}
 */
export class TestimonialsCarousel extends Component {
  requiredRefs = ['scroller'];

  #groupIndex = 0;
  #timerId = /** @type {number | undefined} */ (undefined);
  #paused = false;

  connectedCallback() {
    super.connectedCallback();
    this.updateButtonState();

    const { scroller } = this.refs;
    scroller.addEventListener('scroll', this.updateButtonState, { passive: true });
    window.addEventListener('resize', this.updateButtonState);

    this.addEventListener('mouseenter', this.pause);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('touchstart', this.pause, { passive: true });
    this.addEventListener('touchend', this.resume, { passive: true });
    this.addEventListener('touchcancel', this.resume, { passive: true });

    this.startAutoAdvance();
  }

  disconnectedCallback() {
    this.refs.scroller.removeEventListener('scroll', this.updateButtonState);
    window.removeEventListener('resize', this.updateButtonState);
    this.stopAutoAdvance();
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  startAutoAdvance() {
    if (this.prefersReducedMotion()) return;
    this.stopAutoAdvance();
    this.#timerId = window.setInterval(() => {
      if (this.#paused) return;
      this.advanceGroup();
    }, AUTO_ADVANCE_INTERVAL_MS);
  }

  stopAutoAdvance() {
    if (this.#timerId !== undefined) {
      window.clearInterval(this.#timerId);
      this.#timerId = undefined;
    }
  }

  resetAutoAdvance() {
    this.startAutoAdvance();
  }

  pause = () => {
    this.#paused = true;
  };

  resume = () => {
    this.#paused = false;
  };

  advanceGroup() {
    const { scroller } = this.refs;
    const cards = [...scroller.children];
    const groupCount = Math.ceil(cards.length / GROUP_SIZE);
    if (groupCount <= 1) return;

    this.#groupIndex = (this.#groupIndex + 1) % groupCount;
    this.scrollToGroup(this.#groupIndex);
  }

  /**
   * @param {number} groupIndex
   */
  scrollToGroup(groupIndex) {
    const { scroller } = this.refs;
    const cards = [...scroller.children];
    const target = cards[groupIndex * GROUP_SIZE];
    if (!(target instanceof HTMLElement)) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const delta = targetRect.left - scrollerRect.left;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const nextScrollLeft = Math.max(0, Math.min(scroller.scrollLeft + delta, maxScroll));

    scroller.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
  }

  /**
   * @param {number} direction - -1 for previous, 1 for next
   */
  scrollByCard(direction) {
    const { scroller } = this.refs;
    const card = scroller.children[0];
    if (!(card instanceof HTMLElement)) return;

    const gap = Number.parseFloat(getComputedStyle(scroller).columnGap) || 0;
    const distance = (card.getBoundingClientRect().width + gap) * direction;
    const isRtl = getComputedStyle(this).direction === 'rtl';

    scroller.scrollBy({ left: isRtl ? -distance : distance, behavior: 'smooth' });
  }

  scrollPrev = () => {
    this.scrollByCard(-1);
    this.resetAutoAdvance();
  };

  scrollNext = () => {
    this.scrollByCard(1);
    this.resetAutoAdvance();
  };

  updateButtonState = () => {
    const { scroller, prevButton, nextButton } = this.refs;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft >= maxScroll - 1;

    if (prevButton) prevButton.disabled = maxScroll <= 0 || atStart;
    if (nextButton) nextButton.disabled = maxScroll <= 0 || atEnd;
  };
}

if (!customElements.get('testimonials-carousel')) {
  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
